import { useCallback, useMemo, useState } from 'react'
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Button,
  Card,
  CardContent,
  DateField,
  Label,
  Tooltip,
  TooltipContent,
  TooltipPortal,
  TooltipTrigger,
} from '@atrvd/saas-ui'
import { HelpCircle, Landmark, Loader2, RefreshCw } from 'lucide-react'
import { businessDate } from '@dcp/shared/utils/business-date'
import { formatDate, formatDateTime } from '@dcp/shared/utils/formatters'
import {
  useConnectionInitialImportDone,
  useConnectionOldestTransactionDate,
  isInitialImportDoneError,
  useDeleteBankConnection,
  useSetInitialImportFrom,
} from '@/hooks/useBankConnections'
import {
  useReauthorizeConnection,
  useTriggerLedgerSyncSyncConnection,
} from '@/hooks/useLedgerSync'
import { ConfirmationDialog } from '@/components/admin/ConfirmationDialog'
import { getConnectionSyncActivity } from '@/lib/ledgersync-sync-activity'
import { useSyncStallRerender } from '@/hooks/useSyncStallRerender'
import { RenameConnectionDialog } from './RenameConnectionDialog'
import { SyncFailureIndicator } from './SyncFailureIndicator'
import { ConnectionStatusBadge } from './ConnectionStatusBadge'
import { bankDataAsOfLabel, presentConnectionStatus } from './ledgerSyncConnectedBanks.utils'
import {
  CONNECTION_COPY,
  accountsAndCardsLabel,
  initialImportHint,
  resolveInitialImportDate,
  bankInitials,
} from './connectionManagement.utils'
import type { BankConnectionRow } from '@/lib/db-tenant'
import {
  financialAccountsBasePath,
  type FacilityFinancialAccountsScope,
} from '@/components/financial-accounts/financialAccountsScope'
import { ReadOnlyActionMenu, ReadOnlyTooltip } from '@/components/financial-accounts/ReadOnlyControls'

/** Props for {@link ConnectionDetailHeader}. */
interface ConnectionDetailHeaderProps {
  /**
   * The org-wide connection row. Its status rules read the org-wide
   * `accounts_count` (a `requires_action` connection with no accounts anywhere
   * is a first connect in progress), so it is never the facility-scoped copy.
   */
  connection: BankConnectionRow
  /**
   * The "N accounts & cards" figure of the meta line, display only: the
   * facility's own count on a facility page, `connection.accounts_count`
   * org-wide.
   */
  accountsCount: number
  /**
   * The facility page this connection was opened from; null org-wide. Decides
   * where Reconnect returns to, and an inactive facility makes every action
   * read-only.
   */
  scope: FacilityFinancialAccountsScope | null
  /** Called after the connection is deleted, so the page can navigate away. */
  onDeleted: () => void
}

/**
 * Why the "Initial import from" field is locked (DCP-403): it bounds the
 * connection's first import only, and that has already started.
 */
const INITIAL_IMPORT_DONE_HINT =
  'The initial import has already started for this connection, so this date can no longer be changed. It applies to the first sync only.'

/** Shown while the lock is still being decided — the field is locked meanwhile. */
const INITIAL_IMPORT_CHECKING_HINT = 'Checking whether the initial import has already started…'

/** Shown when the lock could not be decided — the field stays locked. */
const INITIAL_IMPORT_UNKNOWN_HINT =
  "Couldn't check whether the initial import has already started, so this date is locked. Reload the page to try again."

/**
 * The "Initial import from" tooltip, which always explains the field's CURRENT
 * state: why it is locked (DCP-403), or — while editable — what it bounds.
 *
 * @param importDone - the lock query's answer; undefined until it arrives
 * @param importUnknown - the lock query failed, so the field stays locked
 * @param oldestDate - the connection's floor, if the provider has reported one
 * @returns the tooltip text
 */
function importDateHint(
  importDone: boolean | undefined,
  importUnknown: boolean,
  oldestDate: string | null | undefined,
): string {
  if (importDone) return INITIAL_IMPORT_DONE_HINT
  if (importUnknown) return INITIAL_IMPORT_UNKNOWN_HINT
  if (importDone === undefined) return INITIAL_IMPORT_CHECKING_HINT
  // The hint names the FLOOR. With no oldest date on record the provider has
  // not told us how far back it goes, so the sentence would be a guess — the
  // generic half is shown.
  return oldestDate
    ? initialImportHint(formatDate(oldestDate))
    : 'Only transactions from this date to today will be synced during the first import. This applies to the initial sync only.'
}

/**
 * Header card of a connection's detail screen (DCP-324, Screen 2).
 *
 * Carries the bank's identity, its status, the "Initial import from" control
 * (AC#9) and the same connection-level actions the list's kebab offers, so a
 * user who drilled in does not have to go back to rename or delete.
 */
export function ConnectionDetailHeader({
  connection,
  accountsCount,
  scope,
  onDeleted,
}: ConnectionDetailHeaderProps) {
  const readOnly = scope?.readOnly ?? false
  /** This page, so the user lands back where they pressed Reconnect. */
  const reconnectReturnPath = `${financialAccountsBasePath(scope)}/connections/${connection.id}`
  const [logoBroken, setLogoBroken] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)
  const [reconnecting, setReconnecting] = useState(false)

  const setInitialImportFrom = useSetInitialImportFrom()
  const deleteConnection = useDeleteBankConnection()
  const syncConnection = useTriggerLedgerSyncSyncConnection()
  const reauthorize = useReauthorizeConnection()
  /**
   * DCP-401: re-render when a dead run on this connection goes stale — it
   * stops counting as active by age alone, with no new data to re-render on,
   * and would otherwise leave Sync disabled for the rest of the session.
   * Memoised so the hook's timer is re-armed only when the row changes.
   */
  const watchedRows = useMemo(() => [connection], [connection])
  useSyncStallRerender(watchedRows)

  /**
   * The oldest transaction date the provider has across THIS connection's
   * accounts — the floor of the import-date field. Fetched unfiltered (see the
   * hook) rather than reduced from the visible table, whose default filter and
   * lazy paging would give a floor that is too late.
   */
  const { data: oldestDate } = useConnectionOldestTransactionDate(connection.id)

  /**
   * DCP-403: the date bounds the connection's FIRST import only, so once that
   * has started the field is locked. Locked until the answer is known to be "not
   * yet" — while loading, or if it could not be read — so the field is never
   * briefly editable for a connection that is already past its first import
   * (the write path refuses it anyway, with `INITIAL_IMPORT_DONE`).
   *
   * `isError` counts on its own, not only through `data`: a failed BACKGROUND
   * refetch (the query re-asks on every window focus) keeps the last answer in
   * `data`, so an earlier "not yet" would otherwise leave the field editable
   * while the hint says it is locked.
   */
  const { data: initialImportDone, isError: initialImportUnknown } =
    useConnectionInitialImportDone(connection.id)
  const importDateLocked = initialImportDone !== false || initialImportUnknown

  // AC#9. `businessDate()` supplies "today" so the year the default is built
  // from is the BUSINESS day, matching the rest of the banking layer.
  const resolvedImportDate = useMemo(
    () =>
      resolveInitialImportDate({
        stored: connection.initial_import_from,
        oldestTransactionDate: oldestDate ?? null,
        today: businessDate(),
      }),
    [connection.initial_import_from, oldestDate],
  )

  /**
   * What the field currently shows, buffered locally.
   *
   * WHY THE RESOLVED VALUE IS NOT FED STRAIGHT TO `DateField`. The field is
   * typable, and it reports `''` for every INCOMPLETE entry — so on the first
   * digit the value handed back is empty. Passing the resolved value through
   * directly makes that a loop the user cannot win: empty in, the resolver
   * answers with the DEFAULT date, the new prop differs from what the field
   * last reported, and the field discards the draft and re-renders the default.
   * Typing a date would be impossible; only the calendar would work.
   *
   * The buffer breaks it. The draft is the display authority while the user is
   * mid-entry, and it is re-seeded only when the RESOLVED value genuinely
   * changes — a save landing, or the oldest-date query arriving and moving the
   * floor. Adjusting state during render is the React-sanctioned form of
   * "derive from props" and is what `DateField` itself does internally for the
   * same reason.
   *
   * THE "LAST SEEDED VALUE" MUST BE STATE, NOT A REF (DCP-324 bug fix). A
   * `useRef` mutation during render is NOT rolled back the way state is: under
   * `React.StrictMode` (always on for this app, `main.tsx`), React invokes a
   * component's render body twice per commit to surface impure renders. A ref
   * mutated on the first pass STAYS mutated for the second pass, so the second
   * pass sees "no change" (the ref already matches the freshly-resolved value)
   * and skips the state update the FIRST pass had queued — the query resolves
   * (oldest date arrives), but the second, ref-blinded pass wins the commit and
   * the field is left showing the January-1st default forever. `useState` does
   * not have this hazard: both passes read the SAME committed previous value,
   * so the comparison — and the resulting update — is identical either way.
   * (Confirmed live: this exact divergence, reproduced with `pnpm dev`; a
   * production build, which does not double-invoke, did not show it — but the
   * component must be correct under both.)
   */
  const [importDateDraft, setImportDateDraft] = useState(resolvedImportDate.value)
  const [seededImportDate, setSeededImportDate] = useState(resolvedImportDate.value)
  /**
   * Bumped when a refused save puts the stored date back on screen. Part of the
   * `DateField` key, so the field is remounted onto the restored value instead
   * of relying on its in-place resync — see the note on `key` below.
   */
  const [importDateResets, setImportDateResets] = useState(0)
  /**
   * Whether the date input currently HAS FOCUS, i.e. whether the user is in the
   * middle of an entry. Tracked because the re-seed below must not interrupt
   * one — see the note there.
   */
  const [importDateFocused, setImportDateFocused] = useState(false)

  /**
   * RE-SEEDING IS CONSERVATIVE, BECAUSE SAVING IS NOT ATOMIC WITH REFETCHING
   * (DCP-324 input race). The field saves on change, and
   * `setInitialImportFrom.isPending` goes false when the WRITE resolves — while
   * the row this component reads still carries the old date until the query
   * invalidation refetches it. In that window the field is editable again, so a
   * user typing the NEXT date had the arriving (just-saved) value dropped on
   * top of their half-typed entry, and the `key` below remounted the field,
   * taking the focus with it. So:
   *
   * - FOCUSED → nothing happens at all. An arriving value can wait; a
   *   destroyed keystroke cannot be recovered.
   * - BLURRED → the baseline always catches up, so the component can never get
   *   stuck ignoring the server. The visible draft is overwritten only when it
   *   holds nothing worth keeping: a draft still equal to the old baseline is
   *   one the user never touched, and one `DateField` reports as `''` is
   *   incomplete or cleared, so it was never saved. A COMPLETE draft that
   *   differs from both is kept on screen — it is what the user typed, and the
   *   only way it can still differ from what came back is a save the data layer
   *   refused as a bad date (`INVALID_DATE`: in the future, or below the
   *   floor), whose error is already up as a toast; swapping in the old stored
   *   date would hide which value was rejected. The other refusal,
   *   `INITIAL_IMPORT_DONE` (DCP-403), is not left here: nothing about that date
   *   can be corrected, so `handleImportDateChange` puts the stored one back.
   *
   * The initial page load is unaffected: nothing is focused and the draft
   * equals the baseline, so the oldest-date query arriving still re-seeds both
   * (the case the StrictMode tests pin).
   */
  if (resolvedImportDate.value !== seededImportDate && !importDateFocused) {
    setSeededImportDate(resolvedImportDate.value)
    if (importDateDraft === '' || importDateDraft === seededImportDate) {
      setImportDateDraft(resolvedImportDate.value)
    }
  }

  /**
   * Save the import date.
   *
   * ONLY A COMPLETE DATE IS PERSISTED. `DateField` reports `''` for an
   * incomplete entry AND for a cleared one, with nothing to tell the two apart,
   * so treating `''` as "clear" would write `null` on the way to every typed
   * date. Since the field always shows an effective import start — the stored
   * value, or the AC#9 default when there is none — there is no product need to
   * produce an empty one from here: `null` already means "the default", which is
   * what the field would then display anyway. Legacy rows that carry `null`
   * still render, and the data layer keeps accepting it for the sync paths.
   *
   * An out-of-range TYPED value is passed through. `DateField.min`/`max` are
   * affordances on the calendar, not enforcement, and the data layer is the
   * authority — it refuses a future date with a named error, which surfaces as
   * a toast rather than as a silently ignored keystroke.
   *
   * @param next - ISO `YYYY-MM-DD`, or `''` while the entry is incomplete
   */
  const handleImportDateChange = useCallback(
    (next: string) => {
      setImportDateDraft(next)
      if (next === '') return
      // Nothing to write when the value already matches what is stored — a
      // keystroke that reconstructs the same date should not fire a mutation.
      if (next === connection.initial_import_from) return
      setInitialImportFrom.mutate(
        { id: connection.id, date: next },
        {
          onError: (error) => {
            // DCP-403: refused because the initial import has started, so the
            // typed date will never apply. Put the stored one back — unlike a
            // bad date, there is nothing for the user to correct, and leaving
            // it would show a start date the sync is not using. The field
            // re-locks as the hook re-asks the lock.
            if (!isInitialImportDoneError(error)) return
            setImportDateDraft(seededImportDate)
            setImportDateResets((count) => count + 1)
          },
        },
      )
    },
    [connection.id, connection.initial_import_from, seededImportDate, setInitialImportFrom],
  )

  /**
   * Re-consent at the EXISTING connection; see `BankConnectionsSection` for why
   * this is `useReauthorizeConnection` and not the connect flow (DCP-324: the
   * connect flow started a second connection to the same bank).
   */
  const startReconnect = useCallback(() => {
    setReconnecting(true)
    reauthorize.mutate(
      {
        connectionId: connection.id,
        returnPath: reconnectReturnPath,
      },
      {
        onSuccess: ({ reauthUrl, sourceChanged }) => {
          // A migrated bank's URL would create a NEW connection rather than
          // repair this one; the hook has told the user to use "Connect Bank",
          // so release the button instead of navigating.
          if (sourceChanged) {
            setReconnecting(false)
            return
          }
          window.location.href = reauthUrl
        },
        onError: () => setReconnecting(false),
      },
    )
  }, [reauthorize, connection.id, reconnectReturnPath])

  const confirmDelete = useCallback(() => {
    deleteConnection.mutate(connection.id, {
      onSuccess: () => {
        setDeleteOpen(false)
        onDeleted()
      },
      onError: () => setDeleteOpen(false),
    })
  }, [deleteConnection, connection.id, onDeleted])

  /** `source · N accounts & cards`, the static head of the prototype's meta line. */
  const meta = [
    connection.source ? connection.source.toUpperCase() : 'LedgerSync',
    accountsAndCardsLabel(accountsCount),
  ].join(' · ')
  const lastSync = `Last sync: ${
    connection.last_sync_at ? formatDateTime(connection.last_sync_at) : 'Never synced'
  }`
  // How old the bank's figures are, independent of when we last fetched them.
  // Its own line so the meta line's truncation can never cut it off.
  const dataAsOf = bankDataAsOfLabel(connection.data_refreshed_at)
  const status = presentConnectionStatus(connection)

  const canSync = connection.status === 'active'
  // DCP-401: busy for as long as the SERVER says either pipeline of this
  // connection is running (stall-aware), not just for the edge round trip —
  // which is all `isPending` covers, and is kept only for that first instant
  // before the refetched row carries `queued`.
  const activity = getConnectionSyncActivity(connection)
  const syncBusy = activity.active || syncConnection.isPending

  return (
    <Card>
      <CardContent className="flex flex-wrap items-center gap-4 p-6">
        {/* Decorative — the connection's name is the heading right beside it.
            Initials rather than one generic glyph, matching the list and the
            prototype; the glyph survives only for a name with no letters. */}
        <Avatar aria-hidden="true" className="h-11 w-11 shrink-0">
          {connection.logo_url && !logoBroken && (
            <AvatarImage src={connection.logo_url} alt="" onError={() => setLogoBroken(true)} />
          )}
          <AvatarFallback className="text-sm font-semibold">
            {bankInitials(connection.display_name ?? '') || <Landmark className="h-5 w-5 text-muted-foreground" />}
          </AvatarFallback>
        </Avatar>

        <div className="min-w-0 flex-1 basis-60">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="truncate text-xl font-semibold tracking-tight">
              {connection.display_name ?? CONNECTION_COPY.unknownInstitution}
            </h2>
            <ConnectionStatusBadge connection={connection} />
          </div>
          {/* While a sync runs, its phase replaces "Last sync" — the date is
              about to change, and the phase is what explains the wait. */}
          <p className="mt-1 flex min-w-0 items-center gap-1.5 text-sm text-muted-foreground">
            {syncBusy ? (
              <span role="status" className="flex min-w-0 items-center gap-1.5 truncate">
                {meta} ·
                <Loader2 aria-hidden="true" className="h-3.5 w-3.5 shrink-0 animate-spin" />
                {activity.label ?? 'Syncing…'}
              </span>
            ) : (
              <>
                <span className="truncate">{`${meta} · ${lastSync}`}</span>
                <SyncFailureIndicator failures={activity.failures} />
              </>
            )}
          </p>
          {dataAsOf && <p className="mt-0.5 text-xs text-muted-foreground">{dataAsOf}</p>}
          {status.unhealthy && connection.last_error && (
            <p className="mt-1 text-sm text-destructive">{connection.last_error}</p>
          )}
        </div>

        {/* AC#9: "Initial import from" — a typable mm/dd/yyyy field with a
            calendar, not a button-only picker, because the user is entering a
            known date rather than browsing for one. */}
        <div className="flex items-center gap-2">
          <Label htmlFor="initial-import-from" className="whitespace-nowrap text-sm font-normal">
            Initial import from
          </Label>
          <Tooltip>
            <TooltipTrigger asChild>
              <span tabIndex={0} className="inline-flex cursor-help text-muted-foreground">
                <HelpCircle className="h-4 w-4" aria-label="About the initial import date" />
              </span>
            </TooltipTrigger>
            <TooltipPortal>
              <TooltipContent className="max-w-xs">
                {importDateHint(initialImportDone, initialImportUnknown, oldestDate)}
              </TooltipContent>
            </TooltipPortal>
          </Tooltip>
          <ReadOnlyTooltip readOnly={readOnly}>
            <DateField
              // Remounted whenever the SEEDED baseline changes (the oldest-date
              // query resolving, or a save landing) rather than left to update in
              // place. `DateField` (an EXTERNAL `@atrvd/saas-ui` component, not
              // owned by this repo) tracks "did the parent just echo our own
              // change back" with its OWN `useRef`-based previous-value — the
              // exact anti-pattern this file used to have, and it carries the
              // same hazard under `React.StrictMode`'s double-invoked render: a
              // ref mutation from the first pass survives into the second,
              // "purity-check" pass, which then sees no change and skips
              // `DateField`'s own internal resync, leaving IT stuck on the value
              // from before the transition. A `key` change forces React to throw
              // the old instance away and mount a fresh one — which reads `value`
              // through its lazy `useState` initializer, with no ref to desync —
              // sidestepping the hazard entirely rather than depending on a fix
              // to code this repo does not ship.
              //
              // `importDateResets` joins the key for the same reason: a refused
              // save puts the stored date back without the baseline changing.
              key={`${seededImportDate}:${importDateResets}`}
              id="initial-import-from"
              className="w-[150px]"
              value={importDateDraft}
              min={resolvedImportDate.min}
              // Never later than today: a future window cannot contain anything,
              // and the data layer refuses it outright.
              max={businessDate()}
              disabled={importDateLocked || setInitialImportFrom.isPending || readOnly}
              onValueChange={handleImportDateChange}
              // What gates the re-seed above. `DateFieldProps` extends the native
              // input attributes, so these reach the real `<input>`: `onFocus` is
              // passed straight through, and `onBlur` is CHAINED by the component
              // (it runs its own "show the committed value again" handler and
              // then calls this one), so neither is swallowed. A single text
              // input, not a set of segments — so focus does not bounce between
              // sub-fields while the user types, and these two events bracket the
              // whole entry.
              onFocus={() => setImportDateFocused(true)}
              onBlur={() => setImportDateFocused(false)}
            />
          </ReadOnlyTooltip>
        </div>

        <div className="flex items-center gap-2">
          <ReadOnlyTooltip readOnly={readOnly}>
            <Button
              size="sm"
              variant="outline"
              // AC#6: only an active connection has a live link to sync.
              disabled={!canSync || syncBusy || readOnly}
              onClick={() => syncConnection.mutate(connection.id)}
            >
              {syncBusy ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="mr-2 h-4 w-4" />
              )}
              Sync
            </Button>
          </ReadOnlyTooltip>
          <ReadOnlyActionMenu
            readOnly={readOnly}
            ariaLabel="Connection actions"
            items={[
              {
                id: 'rename',
                label: CONNECTION_COPY.renameTitle,
                onClick: () => setRenameOpen(true),
              },
              {
                id: 'reconnect',
                label: 'Reconnect',
                // Needs a canonical connection id to reauthorize, and nothing
                // else — deliberately not gated on status, since re-running it
                // on an ACTIVE connection is how a newly opened account gets
                // picked up (see `BankConnectionsSection`).
                disabled: !connection.ledgersync_connection_id || reconnecting,
                onClick: startReconnect,
              },
              {
                id: 'delete',
                label: CONNECTION_COPY.deleteConnectionCta,
                variant: 'danger' as const,
                onClick: () => setDeleteOpen(true),
              },
            ]}
          />
        </div>
      </CardContent>

      <RenameConnectionDialog
        open={renameOpen}
        onOpenChange={setRenameOpen}
        connection={connection}
      />

      <ConfirmationDialog
        open={deleteOpen}
        onOpenChange={(open) => {
          if (!open && !deleteConnection.isPending) setDeleteOpen(false)
        }}
        title={`Delete ${connection.display_name ?? 'this connection'}?`}
        description={CONNECTION_COPY.deleteConnectionDescription}
        confirmText={CONNECTION_COPY.deleteConnectionCta}
        cancelText="Cancel"
        variant="destructive"
        isLoading={deleteConnection.isPending}
        onConfirm={confirmDelete}
      />
    </Card>
  )
}
